import { useQuery } from '@tanstack/react-query';

import { fetchWithDefaults } from '@/lib/http/client';
import type { CrmObject } from '@/types/crm';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { fetchAggregate, readAggregationNumber } from './aggregate';
import { EVENT_GROUP_TO_TYPES } from './constants';
import type { HighlightResolverResult } from './types';

interface ViewStateEventsResponse {
  events: Array<{ event_type_name: string; occurred_at: string }>;
  pagination: { has_more: boolean };
}

/**
 * Fetch a minimal view-state page for event counting.
 * Returns the raw events array and pagination info.
 */
async function fetchViewStateForCount(
  orgId: number,
  entityId: string
): Promise<ViewStateEventsResponse> {
  const params = new URLSearchParams({
    limit: '50',
    // Header metrics must see associated activity: e.g. contact_touchpoints_30d
    // counts email events that live on associated email-thread entities, never
    // on the record itself. The backend now honours include_associated (it used
    // to ignore it and always fan out), so sending 'false' here would silently
    // zero these counts.
    include_associated: 'true',
  });
  const url = `/api/orgs/${orgId}/crm-events/entities/${entityId}/view-state/?${params}`;
  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch view-state: ${response.statusText}`);
  }
  return response.json() as Promise<ViewStateEventsResponse>;
}

/**
 * Resolve a count of entities or timeline events related to `object`.
 *
 * Case A — entityType is a real EntityTypeName (e.g. 'ahoy_booking'):
 *   POST /aggregate/ filtering by association + optional date window.
 *
 * Case B — entityType === 'event' (sentinel):
 *   GET /view-state/?limit=50 and count matching events client-side.
 *   `filter.associationType` is treated as an event-group key into EVENT_GROUP_TO_TYPES.
 *   Displays '50+' when the backend has more events than the page returned.
 */
export function useHighlightEntityCount(
  orgId: number,
  object: CrmObject,
  definition: Extract<HighlightDefinition, { kind: 'entity_count' }>
): HighlightResolverResult {
  const entityId = object.entity_id;
  const { filter } = definition;
  const entityTypeName = filter.entityType as string;
  const isEventSentinel = entityTypeName === 'event';

  // Stable serialization of filter for the query key
  const filterKey = JSON.stringify(filter);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['highlight', 'entity_count', orgId, entityId, filterKey],
    queryFn: async () => {
      if (isEventSentinel) {
        // Case B: count timeline events client-side via view-state endpoint
        const result = await fetchViewStateForCount(orgId, entityId);
        const eventTypes = EVENT_GROUP_TO_TYPES[filter.associationType] ?? [];
        const eventSet = new Set(eventTypes);

        const hasTypeFilter = eventTypes.length > 0;
        const { sinceDays } = filter;
        const hasDateFilter = sinceDays !== undefined;
        const isFiltered = hasTypeFilter || hasDateFilter;

        let events = hasTypeFilter
          ? result.events.filter(e => eventSet.has(e.event_type_name))
          : result.events;

        if (sinceDays !== undefined) {
          const cutoffMs = Date.now() - sinceDays * 86_400_000;
          events = events.filter(
            e => new Date(e.occurred_at).getTime() >= cutoffMs
          );
        }

        // If backend has more pages, we cannot know the true total. When the
        // count is unfiltered, the raw page size (50) is the exact threshold,
        // so we can confidently display '50+'. When filters reduce the page,
        // report '{matched}+' to communicate "at least this many" without
        // inflation. When zero events matched on this page but more pages
        // exist, we cannot honestly report "0" because later pages may still
        // contain matches — show a non-committal "—" instead.
        if (result.pagination.has_more) {
          if (!isFiltered) {
            return '50+';
          }
          return events.length > 0 ? `${events.length}+` : '—';
        }
        return String(events.length);
      }

      // Case A: aggregate query across a real entity type
      const dateFilter =
        filter.sinceDays !== undefined
          ? [
              {
                property: 'ahoy_created_at',
                operator: 'gte' as const,
                value: new Date(
                  Date.now() - filter.sinceDays * 86_400_000
                ).toISOString(),
              },
            ]
          : [];

      const response = await fetchAggregate(orgId, {
        entity_type_name: entityTypeName,
        aggregations: [{ function: 'count', alias: 'count' }],
        filter_groups: [
          {
            filters: [
              {
                association: filter.associationType,
                operator: 'exact',
                value: entityId,
              },
              ...dateFilter,
            ],
          },
        ],
      });

      return String(readAggregationNumber(response.results, 'count'));
    },
    staleTime: 30_000,
    enabled: !!orgId && !!entityId,
  });

  return {
    value: data ?? null,
    isLoading,
    isError,
  };
}
